import React, { useEffect } from 'react';

import { ContextModal } from './components';
import { MODAL_ZINDEX_MIN } from './constants';
import useModal from './useModal';

export type UseModal = ReturnType<
  typeof useModal
>; /** fallback value for Consumer when no ModalProvider */

export const ModalContext = React.createContext({} as UseModal);

export const ModalProvider = ({ children }) => (
  <ModalContext.Provider value={useModal()}>
    <ModalContext.Consumer>
      {({ stack, popItem }) =>
        stack.map((item, i) => (
          <ContextModal
            key={i + 1}
            item={item}
            isPeak={stack.length === i + 1}
            zIndex={MODAL_ZINDEX_MIN * (i + 2)}
            onClose={popItem}
          />
        ))
      }
    </ModalContext.Consumer>
    {children}
  </ModalContext.Provider>
);

export const useContextModal = () => {
  const modal = React.useContext(ModalContext);

  if (!modal) {
    throw new ReferenceError(
      `'useContextModal' must be used within a 'ModalProvider'`
    );
  }

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        modal.popItem();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modal]);

  return {
    open: modal.pushItem,
    close: modal.popItem,
  };
};

export const useClearContextModals = () => {
  const modal = React.useContext(ModalContext);

  if (!modal) {
    throw new ReferenceError(
      `'useClearContextModals' must be used within a 'ModalProvider'`
    );
  }

  modal.clearItems();
};
